import { Deployment } from '@/types/deployment';
import { House, HouseResponse } from '@/types/house.types';
import { makeFetchOrThrow } from '@/data/helpers/makeFetch';
import { Method } from '@/data/enums/method';
import { QueryKey } from '@/data/enums/queryKeys';
import { Services } from '@/data/enums/services';
import { transformHouseResponse } from '@/data/house/transformHouseResponse';
import { useAppContext } from '@/providers/AppContextProvider/AppContextProvider';
import { useQuery } from '@tanstack/react-query';

type GetHouseOptionsProps = {
    deployment: Deployment;
    houseId: string;
    token: string;
};

/**
 * Fetches house data by house ID
 * @param deployment - The deployment environment
 * @param houseId - The house ID to fetch
 * @param token - Authentication token
 * @returns Promise resolving to House
 */
const getHouse = async ({ deployment, houseId, token }: GetHouseOptionsProps): Promise<House> => {
    if (!houseId) {
        throw new Error('No house ID found. Please contact your administrator.');
    }

    if (!token) {
        throw new Error('No token found. Please contact your administrator.');
    }

    const response = await makeFetchOrThrow<HouseResponse>({
        apiPath: `houses/${houseId}`,
        apiService: Services.SellerPortal,
        deployment,
        method: Method.Get,
        token,
    });

    return transformHouseResponse(response);
};

/**
 * Creates query options for fetching house data
 * Enables SSR prefetch usage and reusability
 * @param deployment - The deployment environment
 * @param houseId - The house ID to fetch
 * @param token - Authentication token
 * @returns Query options object for React Query
 */
export const getHouseOptions = ({ deployment, houseId, token }: GetHouseOptionsProps) => {
    return {
        enabled: Boolean(houseId && token),
        queryFn: () => getHouse({ deployment, houseId, token }),
        queryKey: [QueryKey.House, houseId],
    };
};

/**
 * Hook to fetch house data using the current user's house ID from context
 * @returns React Query result with house data
 */
export const useGetHouseById = (houseId: string) => {
    const { deployment, token } = useAppContext();

    return useQuery(getHouseOptions({ deployment, houseId, token }));
};
